import { CustomFieldRenderProps, F0Box, F0Text, f0FormField } from '@factorialco/f0-react'
import {
  CatalogTrainingRequestFragment,
  GetCatalogTrainingsDocument,
  GetCatalogTrainingsQuery,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

interface TrainingOption {
  id: string
  name: string
}

const extractConnection = (data: GetCatalogTrainingsQuery) => {
  return data.trainings.trainingsConnection
}

const renderEntity = (entity: CatalogTrainingRequestFragment) => (
  <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
    <F0Text content={entity.name} />
  </F0Box>
)

const renderValue = (value: CatalogTrainingRequestFragment | null) => (
  <F0Text content={value ? value.name : i18n.t('common.select_default')} />
)

const renderSkeleton = () => (
  <F0Box display="flex" flexDirection="row" alignItems="center" gap="sm">
    <F0Text content="..." />
  </F0Box>
)

const TrainingSelectorField = ({
  value,
  onChange,
}: {
  value: TrainingOption | undefined
  onChange: (value: TrainingOption | undefined) => void
}) => {
  const selectedTraining = value?.id ? value : null

  return (
    <EntitySelector
      document={GetCatalogTrainingsDocument}
      variables={{ catalog: true }}
      extractConnection={extractConnection}
      renderEntity={renderEntity}
      renderValue={renderValue}
      renderSkeleton={renderSkeleton}
      value={selectedTraining}
      onChange={(selected: CatalogTrainingRequestFragment | null) => {
        if (selected) {
          onChange({ id: String(selected.id), name: selected.name })
        } else {
          onChange(undefined)
        }
      }}
      label={i18n.t('trainings.training_requests.create.training')}
      mandatory={false}
      disabled={false}
      hideSearchIcon
    />
  )
}

export function buildTrainingSelectorSchema() {
  const trainingSchema = z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional()

  return z.object({
    training: f0FormField(trainingSchema, {
      label: i18n.t('trainings.training_requests.create.training'),
      fieldType: 'custom',
      render: ({ value, onChange }: CustomFieldRenderProps<TrainingOption | undefined>) => (
        <TrainingSelectorField value={value} onChange={onChange} />
      ),
    }),
  })
}

export const trainingSelectorSection = () => ({
  title: i18n.t('trainings.training_requests.create.training_step_title'),
  description: i18n.t('trainings.training_requests.create.training_step_description'),
})

export const trainingSelectorDefaults = (defaultTrainingId?: string) => ({
  training: defaultTrainingId ? { id: defaultTrainingId, name: '' } : undefined,
})
